// src/components/GlobalCommandPalette.jsx
//
// App-level command palette (Cmd/Ctrl+K). Jumps to any page the user
// can access, flips the color theme, toggles the sidebar rail, opens
// settings. Stays intentionally small (no backend search) — a later
// upgrade can plug a debounced API call for sites / people / tickets.
//
// Search matches substring on label, path, and keyword tags. Arrow keys
// + Enter navigate; Esc closes.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

const GlobalCommandPalette = ({
  open,
  onClose,
  commands = [],
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = commands.filter((c) => {
      if (c.hidden) return false;
      if (!q) return true;
      const hay = `${c.label} ${c.path || ''} ${(c.tags || []).join(' ')} ${c.group || ''}`.toLowerCase();
      return hay.includes(q);
    });
    // Group headers derived from the `group` field on each command.
    const groups = new Map();
    for (const c of filtered) {
      const key = c.group || 'Actions';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(c);
    }
    const flat = [];
    for (const [groupName, items] of groups) {
      flat.push({ kind: 'header', label: groupName });
      for (const c of items) flat.push({ ...c, kind: 'item' });
    }
    return flat;
  }, [query, commands]);

  const selectable = results.filter((r) => r.kind === 'item');

  const run = (idx) => {
    const item = selectable[idx];
    if (!item) return;
    if (item.onRun) item.onRun();
    else if (item.path) navigate(item.path);
    onClose?.();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(selectable.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      run(activeIdx);
    } else if (e.key === 'Escape') {
      onClose?.();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 1 }}>
          <TextField
            inputRef={inputRef}
            size="small"
            fullWidth
            placeholder="Jump to page, toggle theme, open settings…  (↑↓, Enter, Esc)"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
            onKeyDown={onKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <List sx={{ maxHeight: 460, overflowY: 'auto', py: 0 }}>
          {results.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, display: 'block' }}>
              No matches.
            </Typography>
          ) : (
            (() => {
              let idx = -1;
              return results.map((item, i) => {
                if (item.kind === 'header') {
                  return (
                    <Typography
                      key={`h-${i}`}
                      variant="caption"
                      sx={{
                        px: 2,
                        pt: 1,
                        pb: 0.25,
                        display: 'block',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                      }}
                    >
                      {item.label}
                    </Typography>
                  );
                }
                idx += 1;
                const mine = idx;
                const active = mine === activeIdx;
                return (
                  <ListItemButton
                    key={`r-${i}`}
                    selected={active}
                    onMouseEnter={() => setActiveIdx(mine)}
                    onClick={() => run(mine)}
                  >
                    {item.icon && <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>}
                    <ListItemText
                      primary={item.label}
                      secondary={item.secondary}
                    />
                    {item.shortcut && (
                      <Chip
                        label={item.shortcut}
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1, fontFamily: 'monospace', fontSize: '0.72rem' }}
                      />
                    )}
                  </ListItemButton>
                );
              });
            })()
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalCommandPalette;
